import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/utils/logger";
import { SessionTimeoutWarning } from "@/components/session/SessionTimeoutWarning";
import { AuthErrorBoundary } from "@/components/auth/AuthErrorBoundary";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useAuthStore } from "@/hooks/useAuthStore";
import { TosConsentGate } from "@/components/auth/TosConsentGate";

interface ProtectedRouteProps {
  children: ReactNode;
  requireOrganization?: boolean;
}

function useRouteDiagnostics() {
  const location = useLocation();
  const { userId, isAuthenticated, isLoading } = useAuth();
  const [redirectAttempts, setRedirectAttempts] = useState(0);
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Log route access attempt
  useEffect(() => {
    logger.debug(
      "auth",
      "protected-route-access-attempt",
      `Protected route access attempt at ${location.pathname}`
    );

    // Reset redirect counter when location changes
    setRedirectAttempts(0);
  }, [location.pathname]);

  // Set timeout for loading state
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setLoadingTimeout(true);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timer);
    } else {
      setLoadingTimeout(false);
    }
  }, [isLoading]);

  // Handle unauthenticated access and prevent infinite redirect loops
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setRedirectAttempts((prev) => {
        const newAttempts = prev + 1;

        if (newAttempts > 5) {
          logger.error(
            "auth",
            "redirect-loop-detected",
            "Possible redirect loop detected in authentication flow",
            new Error("Redirect loop detected"),
            { pathname: location.pathname }
          );
          toast.error("Authentication error. Please try refreshing the page.");
        } else {
          logger.warn(
            "auth",
            "unauthorized-access-attempt",
            "Unauthenticated user attempted to access protected route",
            { pathname: location.pathname }
          );
        }

        return newAttempts;
      });
    }
  }, [isLoading, isAuthenticated, location.pathname]);

  // Log successful access to protected route
  useEffect(() => {
    if (!isLoading && isAuthenticated && userId) {
      logger.info(
        "auth",
        "protected-route-accessed",
        `User ${userId.substring(0, 8)}... successfully accessed protected route ${location.pathname}`
      );
    }
  }, [isLoading, isAuthenticated, userId, location.pathname]);

  return { location, isAuthenticated, isLoading, redirectAttempts, loadingTimeout };
}

function ProtectedRouteInner({ children, requireOrganization = true }: ProtectedRouteProps) {
  const { location, isAuthenticated, isLoading, redirectAttempts, loadingTimeout } =
    useRouteDiagnostics();
  const authStore = useAuthStore();

  // Determine what to render based on state
  // Show timeout error if loading takes too long
  if (isLoading && loadingTimeout) return <LoadingTimeout />;

  // Show loading state
  if (isLoading) return <LoadingAuthentication />;

  // Prevent redirect if we've hit too many attempts (avoid infinite loop)
  if (redirectAttempts > 5) return <AuthenticationFailure />;

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate to="/login" state={{ returnUrl: location.pathname + location.search }} replace />
    );
  }

  // Check if user has any organization memberships
  const hasOrganization = authStore.organizationCount > 0;

  // Redirect to org creation if authenticated but no org membership
  if (requireOrganization && !hasOrganization) {
    logger.info(
      "auth",
      "no-org-redirect",
      "User has no organization, redirecting to create-organization"
    );
    return <Navigate to="/create-organization" replace />;
  }

  // Render protected content
  return (
    <AuthErrorBoundary>
      <TosConsentGate>{children}</TosConsentGate>
      <SessionTimeoutWarning />
    </AuthErrorBoundary>
  );
}

const ProtectedRoute = observer(ProtectedRouteInner);
export default ProtectedRoute;

function LoadingTimeout() {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Alert variant="destructive" className="max-w-md">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Loading Timeout</AlertTitle>
        <AlertDescription>
          <p className="mb-3">
            Authentication is taking longer than expected. This might be due to a network issue.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
            <Button variant="default" onClick={() => (window.location.href = "/login")}>
              Go to Login
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

function LoadingAuthentication() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Loading authentication...</p>
        <div className="text-xs text-muted-foreground">Connecting to authentication service...</div>
      </div>
    </div>
  );
}

function AuthenticationFailure() {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Alert variant="destructive" className="max-w-md">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Authentication Error</AlertTitle>
        <AlertDescription>
          <p className="mb-3">
            We're having trouble authenticating your session. This might happen if your session has
            expired.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
            <Button variant="default" onClick={() => (window.location.href = "/login")}>
              Go to Login
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}
